// @ts-strict-ignore
import {
    IImageOverlayOptions,
    ITextOverlayOptions,
    IThreediumAPI,
    IThreediumTemplate,
    OverlayScalingAlignment,
    OverlayScalingAlignmentHorizontal,
    OverlayScalingAlignmentVertical,
    OverlayScalingMode,
} from "constants/product-customizations/threedium";

import {
    IMaterialAttribute,
    IOverlayAttribute,
    IPart,
    IThreediumAttribute,
    IThreediumCustomization,
    IThreediumModel,
} from "constants/product-customizations/customization";

import {
    TCustomizationSelection,
    CustomizationSelectionType,
} from "constants/product-customizations/customization-selection.types";

import _ from "lodash";

export type ObjectsMap = {
    [key: string]: any;
};

export type ThreediumPromiseResult<T = void> = { error: Error } | { result: T };
export type ThreediumPromise<T = void> = Promise<ThreediumPromiseResult<T>>;
export type ThreediumErrorListener<T = any> = (error: any, message?: string) => void;

export const degToRad = (degrees: number): number => (degrees * Math.PI) / 180;

export class Threedium {
    private api: IThreediumAPI;
    private template: IThreediumTemplate;
    private errorListeners: Set<ThreediumErrorListener> = new Set();
    private cameras: ObjectsMap = {};
    private materials: ObjectsMap = {};
    private parts: ObjectsMap = {};
    private overlays: ObjectsMap = {};
    private modifiers: ObjectsMap = {};
    private transitions: ObjectsMap = {};

    public loaded = false;
    public currentAttribute: IThreediumAttribute;

    constructor(api: IThreediumAPI, template: IThreediumTemplate) {
        this.api = api;
        this.template = template;
    }

    attachErrorListener(cb: ThreediumErrorListener) {
        this.errorListeners.add(cb);
    }

    isValidTemplate(model: IThreediumModel) {
        const template = this.template;

        return (
            template &&
            model &&
            template.distID === model.distId &&
            template.projectName === model.projectName &&
            template.solution3DID === model.solutionId &&
            template.solution3DName === model.solutionName
        );
    }

    async loadModel() {
        if (this.loaded) {
            return;
        }

        const loadingRules: [string, string][] = [
            ["getAvailableParts", "parts"],
            ["getAvailableCameras", "cameras"],
            ["getAvailableMaterials", "materials"],
            ["getAvailableModifiers", "modifiers"],
            ["getAvailableOverlays", "overlays"],
            ["getAvailableTransitions", "transitions"],
        ];

        await Promise.all(
            loadingRules.map(
                ([fn, destination]) =>
                    new Promise<void>((resolve, reject) =>
                        this.api[fn]((err, res) => {
                            if (err) {
                                this._onError(err);
                                reject(err);
                                return;
                            }

                            this[destination] = err
                                ? {}
                                : res.reduce((map, item) => {
                                      item.name && (map[item.name] = item);
                                      item.shortName && (map[item.shortName] = item);
                                      return map;
                                  }, {});

                            resolve();
                        })
                    )
            )
        );

        this.loaded = true;
    }

    activateCamera(name: string): ThreediumPromise {
        if (!this.cameras[name]) {
            return this._asyncOnError(new Error(`Invalid camera: ${name}`));
        }

        return new Promise((resolve, reject) => {
            this.api.activateCamera({ name }, this.apiCallback({ resolve }));
        });
    }

    isValidTransition(name: string) {
        return !!this.transitions[name];
    }

    async resetOverlays() {
        const promises = Object.values(this.overlays).map((overlay) => {
            overlay.entries
                ?.filter((entry) => entry.enabled)
                ?.map((entry) => {
                    const args = {
                        options: {
                            enabled: false,
                        },
                        overlay: overlay.name,
                        overlayEntry: entry.name,
                    };

                    return new Promise((resolve, _) => {
                        this.api.updateOverlay(args, this.apiCallback({ resolve }));
                    });
                });
        });

        await Promise.all(_.flatten(promises));
    }

    async setCurrentAttribute(attribute: IThreediumAttribute, animated: boolean) {
        if (this.currentAttribute === attribute) {
            return;
        }

        this.currentAttribute = attribute;
        const transition = attribute?.cameraTransition;
        if (this.isValidTransition(transition)) {
            await this.activateTransition(transition, animated);
        }
    }

    async applyCustomizations(
        attribute: IThreediumAttribute,
        customizations?: IThreediumCustomization[],
        animated: boolean = true
    ): ThreediumPromise {
        if (!this?.loaded) {
            return;
        }

        await this.resetOverlays();

        // transition for current attribute
        await this.setCurrentAttribute(attribute, animated);

        // apply customizations for current template
        for (const customization of customizations || []) {
            const { threedium, selection, model, defaults, binding } = customization;

            if (!(this.isValidTemplate(model) && threedium)) {
                continue;
            }

            const { value: defaultValue, type: defaultType } = defaults || {};
            const { value = defaultValue, type = defaultType } = selection || {};
            const defaultedSelection = selection || defaults || ({} as TCustomizationSelection);

            if (
                value &&
                _.includes([CustomizationSelectionType.COLOR_PICKER, CustomizationSelectionType.COLOR_SWATCH], type) &&
                threedium.material
            ) {
                await this.setMaterialColor(threedium.material, value);
            } else if (
                _.includes([CustomizationSelectionType.PRINTED_TEXT, CustomizationSelectionType.PRINTED_IMAGE], type) &&
                threedium.overlay
            ) {
                await this.setOverlay(
                    threedium.overlay,
                    defaultedSelection,
                    binding.rotation ? degToRad(binding.rotation) : undefined
                );
            } else if (type === CustomizationSelectionType.SELECTABLE) {
                await this.showSelectedPart(threedium.parts, defaultedSelection);
            }
        }
    }

    activateTransition(name: string, animated = true): ThreediumPromise {
        if (!this.transitions[name]) {
            return this._asyncOnError(new Error(`Invalid transition: ${name}`));
        }

        return new Promise((resolve, reject) => {
            // a hack as older threedium versions seem to receive the callback through callback key in first arg object
            const callback: (error: Error, result: void) => void = this.apiCallback({ resolve });
            this.api.activateTransition({ transition: name, duration: animated ? 1000 : 0, callback }, callback);
        });
    }

    setMaterialColor(material: IMaterialAttribute, color: string): ThreediumPromise {
        const name = material.material;
        if (!this.materials[name]) {
            return this._asyncOnError(new Error(`Invalid material name: ${name}`));
        }

        return new Promise((resolve, _) => {
            const changeColor = () =>
                this.api.changeMaterialColor({ material: name, color }, this.apiCallback({ resolve }));
            if (!material.parts?.length) {
                changeColor();
                return;
            }

            this.api.changeMaterial({ parts: material.parts, material: name }, (err) => {
                if (err) {
                    resolve(this._onError(err));
                } else {
                    changeColor();
                }
            });
        });
    }

    setOverlay(
        overlay: IOverlayAttribute,
        selection: TCustomizationSelection,
        rotationRad: number = undefined
    ): ThreediumPromise {
        if (!this.overlays[overlay.overlay]) {
            return this._asyncOnError(new Error(`Invalid overlay name: ${overlay.overlay}`));
        }

        const entry = this.overlays[overlay.overlay].entries.find((e) => overlay.entry == e.name);
        if (entry) {
            entry.enabled = !!selection?.displayValue;
        }
        if (overlay.type === "TEXT") {
            return this.setTextOverlay(overlay, selection, rotationRad);
        } else if (overlay.type === "IMAGE") {
            return this.setImageOverlay(overlay, selection, rotationRad);
        }

        return this._asyncOnError(new Error(`Invalid overlay type: ${overlay.type}, name: ${overlay.overlay}`));
    }

    showSelectedPart(parts: IPart[], selection: TCustomizationSelection): ThreediumPromise {
        const selectedId = selection?.target?.destinationId;
        const hiddenPartsNames = parts.filter((p) => p._id !== selectedId).map((p) => p.part);
        const selectedPart = parts.find((p) => p._id === selectedId);

        return new Promise((resolve, _) => {
            this.api.hideParts({ parts: hiddenPartsNames }, (err, show) => {
                if (err) {
                    resolve(this._onError(err));
                } else if (selectedPart?.part) {
                    this.api.showParts(
                        { partObjects: [{ parts: [selectedPart?.part] }] },
                        this.apiCallback({ resolve })
                    );
                }
            });
        });
    }

    private setTextOverlay(
        overlay: IOverlayAttribute,
        selection: TCustomizationSelection,
        rotationRad: number = undefined,
        scalingMode: OverlayScalingMode = OverlayScalingMode.FILL,
        scalingAlignment: OverlayScalingAlignment = [
            OverlayScalingAlignmentHorizontal.CENTER,
            OverlayScalingAlignmentVertical.CENTER,
        ]
    ): ThreediumPromise {
        const rotation = rotationRad;

        const options: ITextOverlayOptions = {
            text: selection?.value || "",
            enabled: !!selection?.value,
            rotation,
            scalingMode,
            scalingAlignment,
        };

        const args = {
            overlay: overlay.overlay,
            overlayEntry: overlay.entry,
            options,
        };

        for (const attribute of selection.nestedValues || []) {
            if (_.isEmpty(attribute.value)) {
                continue;
            }

            const { valueKey, value } = attribute;
            if (_.includes(["colorPicker", "colorSwatch"], valueKey)) {
                options.fontColor = value;
            } else if (valueKey === "fontSize") {
                options.fontScale = +value;
            } else if (valueKey === "font") {
                options.fontUrl = value;
            }
        }

        return new Promise((resolve, _) => {
            this.api.updateOverlay(args, this.apiCallback({ resolve }));
        });
    }

    private setImageOverlay(
        overlay: IOverlayAttribute,
        selection: TCustomizationSelection,
        rotationRad: number = undefined,
        scalingMode: OverlayScalingMode = OverlayScalingMode.SCALE_TO_FIT,
        scalingAlignment: OverlayScalingAlignment = [
            OverlayScalingAlignmentHorizontal.CENTER,
            OverlayScalingAlignmentVertical.CENTER,
        ]
    ): ThreediumPromise {
        const rotation = rotationRad;

        const options: IImageOverlayOptions = {
            imageUrl: selection?.displayValue || "",
            enabled: !!selection?.displayValue,
            rotation,
            scalingMode,
            scalingAlignment,
        };

        const args = {
            overlay: overlay.overlay,
            overlayEntry: overlay.entry,
            options,
        };

        return new Promise((resolve, _) => {
            this.api.updateOverlay(args, this.apiCallback({ resolve }));
        });
    }

    private _onError<T = void>(error: Error, message?: string): ThreediumPromiseResult<T> {
        for (const cb of this.errorListeners) {
            cb(error, message);
        }

        return { error };
    }

    private async _asyncOnError<T = void>(error: Error, message?: string): ThreediumPromise<T> {
        return this._onError(error, message);
    }

    private apiCallback<T = void>({
        args,
        resolve,
    }: { args?: { [key: string]: any }; resolve?: (result: ThreediumPromiseResult<T>) => void } = {}) {
        return (error: Error, result: T) => {
            if (error) {
                this._onError(error, args && JSON.stringify(args));
            }

            resolve?.({ result, error });
        };
    }
}
